import React from "react";
import PropTypes from "prop-types";

const Pagination = ({ pagination, onPageChange, showInfo = true }) => {
  const { currentPage, lastPage, total, from, to } = pagination;

  // Generate page numbers array
  const getPageNumbers = () => {
    const delta = 2; // Number of pages to show before and after current page
    const range = [];
    const rangeWithDots = [];

    // Always show first page
    range.push(1);

    for (let i = currentPage - delta; i <= currentPage + delta; i++) {
      if (i > 1 && i < lastPage) {
        range.push(i);
      }
    }

    // Always show last page
    if (lastPage > 1) {
      range.push(lastPage);
    }

    // Add dots between ranges
    let l;
    for (let i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push("...");
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  };

  // Don't show page controls when there's only one page
  const showPageControls = lastPage > 1;

  if (!showPageControls && (!showInfo || total === 0)) return null;

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-2">
      {/* Pagination Info */}
      {showInfo && total > 0 && (
        <div className="text-sm text-gray-700">
          Showing{" "}
          <span className="font-medium">
            {from} to {to}
          </span>{" "}
          of <span className="font-medium">{total}</span> results
        </div>
      )}

      {/* Pagination Controls */}
      {showPageControls && (
      <div className="flex items-center gap-2">
        {/* Previous Button */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-2 py-1 text-sm rounded-md border ${
            currentPage === 1
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-white text-gray-700 hover:bg-gray-50"
          }`}
        >
          Previous
        </button>

        {/* Page Numbers */}
        <div className="flex items-center gap-1">
          {getPageNumbers().map((pageNumber, index) => (
            <button
              key={index}
              onClick={() =>
                pageNumber !== "..." ? onPageChange(pageNumber) : null
              }
              disabled={pageNumber === "..."}
              className={`px-3 py-1 text-sm rounded-md border ${
                pageNumber === currentPage
                  ? "bg-primary text-white border-primary"
                  : pageNumber === "..."
                  ? "cursor-default bg-white text-gray-700"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              {pageNumber}
            </button>
          ))}
        </div>

        {/* Next Button */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === lastPage}
          className={`px-2 py-1 text-sm rounded-md border ${
            currentPage === lastPage
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-white text-gray-700 hover:bg-gray-50"
          }`}
        >
          Next
        </button>
      </div>
      )}
    </div>
  );
};

Pagination.propTypes = {
  pagination: PropTypes.shape({
    currentPage: PropTypes.number.isRequired,
    lastPage: PropTypes.number.isRequired,
    perPage: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    from: PropTypes.number.isRequired,
    to: PropTypes.number.isRequired,
  }).isRequired,
  onPageChange: PropTypes.func.isRequired,
  showInfo: PropTypes.bool,
};

export default Pagination;
